import axios, { type AxiosError } from 'axios';
import { validateAttachmentBatch } from '@21k/shared';

import type {
  SystemRole,
  Asset,
  AuthUser,
  ChannelConfig,
  ChannelType,
  ChannelTypeOption,
  Comment,
  CreateSystemRolePayload,
  CreateChannelConfigPayload,
  CreateChannelTypePayload,
  CreateDepartmentPayload,
  CreateSlaRulePayload,
  CreateTicketPayload,
  CreateTicketLabelPayload,
  CreateTicketCategoryPayload,
  CreateTicketStatusPayload,
  CreateUserPayload,
  DepartmentDetail,
  DepartmentSummary,
  DashboardPayload,
  AssignableTeamGroup,
  EntityLearnerResult,
  EntityParentResult,
  EnrollmentLookupResult,
  EnrollmentSourceRulesResponse,
  EntitySourceDetail,
  EntitySourcePrefix,
  EntitySourceSummary,
  EscalationTargetSummary,
  IntakeResolveResult,
  IntakeRouteSummary,
  LearnerContext,
  LinkedSource,
  ParentResult,
  PaginatedComments,
  PaginatedResult,
  ReportType,
  SlaRule,
  CreateTeamPayload,
  TeamDetail,
  TeamListItem,
  TeamMemberOption,
  TicketAudit,
  TicketCategoryDefinition,
  TicketCategoryRef,
  TicketDetail,
  TicketFilters,
  TicketLabelDefinition,
  TicketLabelRef,
  TicketListItem,
  TicketOwnership,
  TicketStatusDefinition,
  TicketStatusRef,
  TicketStatusWorkflow,
  UpdateTicketLabelPayload,
  UpdateTicketCategoryPayload,
  UpdateTicketStatusPayload,
  UpdateSystemRolePayload,
  UpdateChannelConfigPayload,
  UpdateChannelTypePayload,
  UpdateDepartmentPayload,
  UpdateSlaRulePayload,
  UpdateTeamPayload,
  UpdateUserPayload,
  UpdateTicketAssigneeResult,
  StaffBulkImportResult,
  UserOption,
  UserProfile,
  LinkedProfile,
  LinkedProfileChild,
  LinkedProfileFull,
  LinkedProfileTicketsResult,
  CrossSystemPresence,
  NotificationListResponse,
} from './types';
import { notifyUnopenedQueueChanged } from '../utils/unopenedQueue';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

const http = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

http.interceptors.request.use((config) => {
  const token = localStorage.getItem('ots_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function unwrap<T>(res: { data: { data: T } | T }): T {
  const body = res.data as { data?: T };
  return (body.data ?? res.data) as T;
}

export function getApiErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const ax = err as AxiosError<{ error?: string; retryAfterSeconds?: number }>;
    const msg = ax.response?.data?.error;
    if (ax.response?.status === 429 && ax.response.data?.retryAfterSeconds) {
      return `${msg ?? 'Locked'} — retry in ${ax.response.data.retryAfterSeconds}s`;
    }
    if (msg) return msg;
    if (ax.code === 'ERR_NETWORK') {
      return 'Cannot reach API — is the backend running on port 3001?';
    }
    return ax.message ?? 'Request failed';
  }
  if (err instanceof Error) return err.message;
  return 'Request failed';
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export async function requestOtp(email: string): Promise<{ devOtp?: string }> {
  const res = await http.post<{ message: string; devOtp?: string }>('/auth/request-otp', {
    email,
  });
  return { devOtp: res.data.devOtp };
}

export async function verifyOtp(
  email: string,
  code: string,
): Promise<{ token: string; user: AuthUser }> {
  const res = await http.post<{ token: string; user: AuthUser }>('/auth/verify-otp', {
    email,
    code,
  });
  return res.data;
}

export async function getMe(): Promise<UserProfile> {
  const res = await http.get<{ user: UserProfile }>('/auth/me');
  return res.data.user;
}

export async function exchangeSsoToken(
  token: string,
): Promise<{ token: string; user: AuthUser }> {
  const res = await http.post<{ token: string; user: AuthUser }>('/auth/sso-exchange', {
    token,
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// System roles (Role Management)
// ---------------------------------------------------------------------------

export async function listSystemRoles(): Promise<SystemRole[]> {
  const res = await http.get<{ data: SystemRole[] }>('/system-roles');
  return unwrap(res);
}

export async function getSystemRole(id: string): Promise<SystemRole> {
  const res = await http.get<{ data: SystemRole }>(`/system-roles/${id}`);
  return unwrap(res);
}

export async function createSystemRole(payload: CreateSystemRolePayload): Promise<SystemRole> {
  const res = await http.post<{ data: SystemRole }>('/system-roles', payload);
  return unwrap(res);
}

export async function updateSystemRole(
  id: string,
  payload: UpdateSystemRolePayload,
): Promise<SystemRole> {
  const res = await http.patch<{ data: SystemRole }>(`/system-roles/${id}`, payload);
  return unwrap(res);
}

export async function deactivateSystemRole(id: string): Promise<SystemRole> {
  const res = await http.delete<{ data: SystemRole }>(`/system-roles/${id}`);
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// Departments
// ---------------------------------------------------------------------------

export async function listDepartments(): Promise<DepartmentSummary[]> {
  const res = await http.get<{ data: DepartmentSummary[] }>('/departments');
  return unwrap(res);
}

export async function listManageDepartments(): Promise<DepartmentDetail[]> {
  const res = await http.get<{ data: DepartmentDetail[] }>('/departments/manage');
  return unwrap(res);
}

export async function getDepartment(id: string): Promise<DepartmentDetail> {
  const res = await http.get<{ data: DepartmentDetail }>(`/departments/${id}`);
  return unwrap(res);
}

export async function createDepartment(
  payload: CreateDepartmentPayload,
): Promise<DepartmentDetail> {
  const res = await http.post<{ data: DepartmentDetail }>('/departments', payload);
  return unwrap(res);
}

export async function updateDepartment(
  id: string,
  payload: UpdateDepartmentPayload,
): Promise<DepartmentDetail> {
  const res = await http.patch<{ data: DepartmentDetail }>(`/departments/${id}`, payload);
  return unwrap(res);
}

export async function listEscalationTargets(
  departmentId: string,
): Promise<EscalationTargetSummary[]> {
  const res = await http.get<{ data: EscalationTargetSummary[] }>(
    `/departments/${departmentId}/escalation-targets`,
  );
  return unwrap(res);
}

export async function listEscalationOptions(
  departmentId: string,
): Promise<DepartmentSummary[]> {
  const res = await http.get<{ data: DepartmentSummary[] }>(
    `/departments/${departmentId}/escalation-options`,
  );
  return unwrap(res);
}

export async function addEscalationTarget(
  departmentId: string,
  payload: { toDepartmentId: string; label?: string; sortOrder?: number },
): Promise<EscalationTargetSummary> {
  const res = await http.post<{ data: EscalationTargetSummary }>(
    `/departments/${departmentId}/escalation-targets`,
    payload,
  );
  return unwrap(res);
}

export async function removeEscalationTarget(
  departmentId: string,
  targetId: string,
): Promise<void> {
  await http.delete(`/departments/${departmentId}/escalation-targets/${targetId}`);
}

// ---------------------------------------------------------------------------
// Channel configs
// ---------------------------------------------------------------------------

export async function listChannelConfigs(departmentId?: string): Promise<ChannelConfig[]> {
  const res = await http.get<{ data: ChannelConfig[] }>('/channel-configs', {
    params: departmentId ? { departmentId } : undefined,
  });
  return unwrap(res);
}

export async function getChannelConfig(id: string): Promise<ChannelConfig> {
  const res = await http.get<{ data: ChannelConfig }>(`/channel-configs/${id}`);
  return unwrap(res);
}

export async function createChannelConfig(
  payload: CreateChannelConfigPayload,
): Promise<ChannelConfig> {
  const res = await http.post<{ data: ChannelConfig }>('/channel-configs', payload);
  return unwrap(res);
}

export async function updateChannelConfig(
  id: string,
  payload: UpdateChannelConfigPayload,
): Promise<ChannelConfig> {
  const res = await http.patch<{ data: ChannelConfig }>(`/channel-configs/${id}`, payload);
  return unwrap(res);
}

export async function deleteChannelConfig(id: string): Promise<void> {
  await http.delete(`/channel-configs/${id}`);
}

// ---------------------------------------------------------------------------
// Channel types (master list)
// ---------------------------------------------------------------------------

export async function listChannelTypeOptions(): Promise<ChannelTypeOption[]> {
  const res = await http.get<{ data: ChannelTypeOption[] }>('/channel-types/options');
  return unwrap(res);
}

export async function listChannelTypes(): Promise<ChannelType[]> {
  const res = await http.get<{ data: ChannelType[] }>('/channel-types');
  return unwrap(res);
}

export async function getChannelType(id: string): Promise<ChannelType> {
  const res = await http.get<{ data: ChannelType }>(`/channel-types/${id}`);
  return unwrap(res);
}

export async function createChannelType(
  payload: CreateChannelTypePayload,
): Promise<ChannelType> {
  const res = await http.post<{ data: ChannelType }>('/channel-types', payload);
  return unwrap(res);
}

export async function updateChannelType(
  id: string,
  payload: UpdateChannelTypePayload,
): Promise<ChannelType> {
  const res = await http.patch<{ data: ChannelType }>(`/channel-types/${id}`, payload);
  return unwrap(res);
}

export async function deactivateChannelType(id: string): Promise<ChannelType> {
  const res = await http.delete<{ data: ChannelType }>(`/channel-types/${id}`);
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// Ticket labels
// ---------------------------------------------------------------------------

export async function listTicketLabelOptions(): Promise<TicketLabelRef[]> {
  const res = await http.get<{ data: TicketLabelRef[] }>('/ticket-labels/options');
  return unwrap(res);
}

export async function listTicketLabels(): Promise<TicketLabelDefinition[]> {
  const res = await http.get<{ data: TicketLabelDefinition[] }>('/ticket-labels');
  return unwrap(res);
}

export async function getTicketLabel(id: string): Promise<TicketLabelDefinition> {
  const res = await http.get<{ data: TicketLabelDefinition }>(`/ticket-labels/${id}`);
  return unwrap(res);
}

export async function createTicketLabel(
  payload: CreateTicketLabelPayload,
): Promise<TicketLabelDefinition> {
  const res = await http.post<{ data: TicketLabelDefinition }>('/ticket-labels', payload);
  return unwrap(res);
}

export async function updateTicketLabelDefinition(
  id: string,
  payload: UpdateTicketLabelPayload,
): Promise<TicketLabelDefinition> {
  const res = await http.patch<{ data: TicketLabelDefinition }>(`/ticket-labels/${id}`, payload);
  return unwrap(res);
}

export async function deactivateTicketLabel(id: string): Promise<TicketLabelDefinition> {
  const res = await http.delete<{ data: TicketLabelDefinition }>(`/ticket-labels/${id}`);
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// Ticket categories
// ---------------------------------------------------------------------------

export async function listTicketCategoryOptions(): Promise<TicketCategoryRef[]> {
  const res = await http.get<{ data: TicketCategoryRef[] }>('/ticket-categories/options');
  return unwrap(res);
}

export async function listTicketCategories(): Promise<TicketCategoryDefinition[]> {
  const res = await http.get<{ data: TicketCategoryDefinition[] }>('/ticket-categories');
  return unwrap(res);
}

export async function getTicketCategory(id: string): Promise<TicketCategoryDefinition> {
  const res = await http.get<{ data: TicketCategoryDefinition }>(`/ticket-categories/${id}`);
  return unwrap(res);
}

export async function createTicketCategory(
  payload: CreateTicketCategoryPayload,
): Promise<TicketCategoryDefinition> {
  const res = await http.post<{ data: TicketCategoryDefinition }>('/ticket-categories', payload);
  return unwrap(res);
}

export async function updateTicketCategoryDefinition(
  id: string,
  payload: UpdateTicketCategoryPayload,
): Promise<TicketCategoryDefinition> {
  const res = await http.patch<{ data: TicketCategoryDefinition }>(`/ticket-categories/${id}`, payload);
  return unwrap(res);
}

export async function deactivateTicketCategory(id: string): Promise<TicketCategoryDefinition> {
  const res = await http.delete<{ data: TicketCategoryDefinition }>(`/ticket-categories/${id}`);
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// Ticket statuses
// ---------------------------------------------------------------------------

export async function listTicketStatusOptions(): Promise<TicketStatusRef[]> {
  const res = await http.get<{ data: TicketStatusRef[] }>('/ticket-statuses/options');
  return unwrap(res);
}

export async function getTicketStatusWorkflow(): Promise<TicketStatusWorkflow> {
  const res = await http.get<{ data: TicketStatusWorkflow }>('/ticket-statuses/workflow');
  return unwrap(res);
}

export async function listTicketStatuses(): Promise<TicketStatusDefinition[]> {
  const res = await http.get<{ data: TicketStatusDefinition[] }>('/ticket-statuses');
  return unwrap(res);
}

export async function getTicketStatus(id: string): Promise<TicketStatusDefinition> {
  const res = await http.get<{ data: TicketStatusDefinition }>(`/ticket-statuses/${id}`);
  return unwrap(res);
}

export async function createTicketStatus(
  payload: CreateTicketStatusPayload,
): Promise<TicketStatusDefinition> {
  const res = await http.post<{ data: TicketStatusDefinition }>('/ticket-statuses', payload);
  return unwrap(res);
}

export async function updateTicketStatusDefinition(
  id: string,
  payload: UpdateTicketStatusPayload,
): Promise<TicketStatusDefinition> {
  const res = await http.patch<{ data: TicketStatusDefinition }>(`/ticket-statuses/${id}`, payload);
  return unwrap(res);
}

export async function deactivateTicketStatus(id: string): Promise<TicketStatusDefinition> {
  const res = await http.delete<{ data: TicketStatusDefinition }>(`/ticket-statuses/${id}`);
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// SLA rules
// ---------------------------------------------------------------------------

export async function listSlaRules(departmentId?: string): Promise<SlaRule[]> {
  const res = await http.get<{ data: SlaRule[] }>('/sla', {
    params: departmentId ? { departmentId } : undefined,
  });
  return unwrap(res);
}

export async function getSlaRule(id: string): Promise<SlaRule> {
  const res = await http.get<{ data: SlaRule }>(`/sla/${id}`);
  return unwrap(res);
}

export async function createSlaRule(payload: CreateSlaRulePayload): Promise<SlaRule> {
  const res = await http.post<{ data: SlaRule }>('/sla', payload);
  return unwrap(res);
}

export async function updateSlaRule(
  id: string,
  payload: UpdateSlaRulePayload,
): Promise<SlaRule> {
  const res = await http.patch<{ data: SlaRule }>(`/sla/${id}`, payload);
  return unwrap(res);
}

export async function deleteSlaRule(id: string): Promise<void> {
  await http.delete(`/sla/${id}`);
}

// ---------------------------------------------------------------------------
// Teams
// ---------------------------------------------------------------------------

export async function listTeams(departmentId: string): Promise<TeamListItem[]> {
  const res = await http.get<{ data: TeamListItem[] }>('/teams', {
    params: { departmentId },
  });
  return unwrap(res);
}

export async function getTeam(id: string): Promise<TeamDetail> {
  const res = await http.get<{ data: TeamDetail }>(`/teams/${id}`);
  return unwrap(res);
}

export async function createTeam(payload: CreateTeamPayload): Promise<TeamDetail> {
  const res = await http.post<{ data: TeamDetail }>('/teams', payload);
  return unwrap(res);
}

export async function updateTeam(id: string, payload: UpdateTeamPayload): Promise<TeamDetail> {
  const res = await http.patch<{ data: TeamDetail }>(`/teams/${id}`, payload);
  return unwrap(res);
}

export async function addTeamMember(
  teamId: string,
  userId: string,
  options?: { moveFromOtherTeams?: boolean },
): Promise<TeamDetail> {
  const res = await http.post<{ data: TeamDetail }>(`/teams/${teamId}/members`, {
    userId,
    ...(options?.moveFromOtherTeams ? { moveFromOtherTeams: true } : {}),
  });
  return unwrap(res);
}

export async function removeTeamMember(teamId: string, userId: string): Promise<void> {
  await http.delete(`/teams/${teamId}/members/${userId}`);
}

// ---------------------------------------------------------------------------
// Tickets
// ---------------------------------------------------------------------------

export async function listTickets(
  filters: TicketFilters = {},
): Promise<PaginatedResult<TicketListItem>> {
  const res = await http.get<{ data: PaginatedResult<TicketListItem> | TicketListItem[] }>(
    '/tickets',
    { params: filters },
  );
  const payload = unwrap(res);
  if (Array.isArray(payload)) {
    return {
      items: payload,
      total: payload.length,
      page: filters.page ?? 1,
      pageSize: payload.length || 20,
      hasMore: false,
    };
  }
  const items = Array.isArray(payload?.items) ? payload.items : [];
  return {
    items,
    total: typeof payload?.total === 'number' ? payload.total : items.length,
    page: payload?.page ?? filters.page ?? 1,
    pageSize: payload?.pageSize ?? 20,
    hasMore: Boolean(payload?.hasMore),
  };
}

export async function getUnopenedTicketCount(): Promise<number> {
  const res = await http.get<{ data: { count: number } }>('/tickets/unopened-count');
  return unwrap(res).count;
}

export async function getTicket(id: string): Promise<TicketDetail> {
  const res = await http.get<{ data: TicketDetail }>(`/tickets/${id}`);
  const ticket = unwrap(res);
  notifyUnopenedQueueChanged();
  return ticket;
}

export async function getLinkedProfile(ticketId: string): Promise<LinkedProfile | null> {
  const res = await http.get<{ data: LinkedProfile | null }>(
    `/tickets/${ticketId}/linked-profile`,
  );
  return unwrap(res);
}

export async function getLinkedProfileFull(ticketId: string): Promise<LinkedProfileFull | null> {
  const res = await http.get<{ data: LinkedProfileFull | null }>(
    `/tickets/${ticketId}/linked-profile/full`,
  );
  return unwrap(res);
}

export async function getCrossSystemPresence(ticketId: string): Promise<CrossSystemPresence> {
  const res = await http.get<{ data: CrossSystemPresence }>(
    `/tickets/${ticketId}/linked-profile/cross`,
  );
  return unwrap(res);
}

export async function getLinkedProfileTickets(
  ticketId: string,
): Promise<LinkedProfileTicketsResult> {
  const res = await http.get<{ data: LinkedProfileTicketsResult }>(
    `/tickets/${ticketId}/linked-profile/tickets`,
  );
  return unwrap(res);
}

export async function getLinkedProfileChildren(
  ticketId: string,
): Promise<{ children: LinkedProfileChild[] }> {
  const res = await http.get<{ data: { children: LinkedProfileChild[] } }>(
    `/tickets/${ticketId}/linked-profile/children`,
  );
  return unwrap(res);
}

export async function getLinkedProfileSiblings(
  ticketId: string,
): Promise<{ siblings: LinkedProfileChild[] }> {
  const res = await http.get<{ data: { siblings: LinkedProfileChild[] } }>(
    `/tickets/${ticketId}/linked-profile/siblings`,
  );
  return unwrap(res);
}

export async function patchTicketLinkedLearner(
  ticketId: string,
  body: { learnerId: string; enrollmentId: string },
): Promise<TicketDetail> {
  const res = await http.patch<{ data: TicketDetail }>(
    `/tickets/${ticketId}/linked-learner`,
    body,
  );
  return unwrap(res);
}

export async function createTicket(payload: CreateTicketPayload): Promise<TicketDetail> {
  const res = await http.post<{ data: TicketDetail }>('/tickets', payload);
  return unwrap(res);
}

export async function reopenTicket(id: string): Promise<TicketDetail> {
  const res = await http.post<{ data: TicketDetail }>(`/tickets/${id}/reopen`);
  return unwrap(res);
}

export async function updateTicketStatus(
  id: string,
  statusId: string,
  comment: string,
): Promise<TicketDetail> {
  const res = await http.patch<{ data: TicketDetail }>(`/tickets/${id}/status`, {
    statusId,
    comment,
  });
  return unwrap(res);
}

export async function updateTicketLabels(id: string, labelIds: string[]): Promise<TicketDetail> {
  const res = await http.patch<{ data: TicketDetail }>(`/tickets/${id}/labels`, { labelIds });
  return unwrap(res);
}

/** @deprecated Use updateTicketLabels */
export async function updateTicketLabel(id: string, labelId: string): Promise<TicketDetail> {
  return updateTicketLabels(id, [labelId]);
}

export async function updateTicketPriority(
  id: string,
  priority: string,
): Promise<TicketDetail> {
  const res = await http.patch<{ data: TicketDetail }>(`/tickets/${id}/priority`, {
    priority,
  });
  return unwrap(res);
}

export async function updateTicketCategory(
  id: string,
  categoryId: string,
): Promise<TicketDetail> {
  const res = await http.patch<{ data: TicketDetail }>(`/tickets/${id}/category`, {
    categoryId,
  });
  return unwrap(res);
}

export async function updateTicketAssignee(
  id: string,
  assignedToId: string | null,
): Promise<UpdateTicketAssigneeResult> {
  const res = await http.patch<{ data: UpdateTicketAssigneeResult }>(`/tickets/${id}/assignee`, {
    assignedToId,
  });
  return unwrap(res);
}

export async function updateTicketDetails(
  id: string,
  data: { title?: string; description?: string },
): Promise<TicketDetail> {
  const res = await http.patch<{ data: TicketDetail }>(`/tickets/${id}`, data);
  return unwrap(res);
}

export async function getTicketAudit(id: string): Promise<TicketAudit[]> {
  const res = await http.get<{ data: TicketAudit[] }>(`/tickets/${id}/audit`);
  return unwrap(res);
}

export async function closeOnBehalf(id: string, comment?: string): Promise<TicketDetail> {
  const res = await http.post<{ data: TicketDetail }>(`/tickets/${id}/close-on-behalf`, {
    comment,
  });
  return unwrap(res);
}

export async function escalateWithinDept(
  id: string,
  payload: { toTeamId: string; toUserId?: string; escalationNote?: string },
): Promise<TicketOwnership> {
  const res = await http.patch<{ data: TicketOwnership }>(
    `/tickets/${id}/escalate-within-dept`,
    payload,
  );
  return unwrap(res);
}

export async function escalateCrossDept(
  id: string,
  payload: { toDepartmentId: string; escalationNote: string; handoffParentFacing?: boolean },
): Promise<TicketOwnership> {
  const res = await http.patch<{ data: TicketOwnership }>(
    `/tickets/${id}/escalate-cross-dept`,
    payload,
  );
  return unwrap(res);
}

export async function resolveEscalation(id: string): Promise<TicketOwnership> {
  const res = await http.patch<{ data: TicketOwnership }>(
    `/tickets/${id}/resolve-escalation`,
  );
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// Comments & Assets
// ---------------------------------------------------------------------------

export async function searchMentionUsers(
  q: string,
): Promise<Array<{ id: string; firstName: string; lastName: string; email: string }>> {
  const res = await http.get<{
    data: Array<{ id: string; firstName: string; lastName: string; email: string }>;
  }>('/comments/mention-users', { params: { q } });
  return unwrap(res);
}

export async function listComments(
  ticketId: string,
  params?: { page?: number; pageSize?: number; sort?: 'oldest' | 'newest' },
): Promise<PaginatedComments> {
  const res = await http.get<{ data: PaginatedComments }>(`/comments/ticket/${ticketId}`, {
    params,
  });
  return unwrap(res);
}

export async function createComment(
  ticketId: string,
  content: string,
  options?: {
    parentId?: string;
    visibility?: 'CUSTOMER_VISIBLE' | 'INTERNAL_NOTE';
  },
): Promise<Comment> {
  const res = await http.post<{ data: Comment }>(`/comments/ticket/${ticketId}`, {
    content,
    parentId: options?.parentId,
    visibility: options?.visibility,
  });
  return unwrap(res);
}

export async function listAssets(ticketId: string): Promise<Asset[]> {
  const res = await http.get<{ data: Asset[] }>(`/tickets/${ticketId}/assets`);
  return unwrap(res);
}

export async function uploadAsset(
  ticketId: string,
  file: File,
  commentId?: string,
): Promise<Asset> {
  const form = new FormData();
  form.append('file', file);
  if (commentId) form.append('commentId', commentId);

  const res = await http.postForm<{ data: Asset }>(`/tickets/${ticketId}/assets`, form);
  return unwrap(res);
}

export async function uploadAssets(
  ticketId: string,
  files: File[],
  commentId?: string,
): Promise<Asset[]> {
  const batchError = validateAttachmentBatch(files);
  if (batchError) throw new Error(batchError);

  const results: Asset[] = [];
  for (const file of files) {
    results.push(await uploadAsset(ticketId, file, commentId));
  }
  return results;
}

export async function getAssetUrl(
  assetId: string,
  ticketId?: string,
): Promise<{ url: string }> {
  if (ticketId) {
    const res = await http.get<{ data: { url: string } }>(
      `/tickets/${ticketId}/assets/${assetId}/url`,
    );
    return unwrap(res);
  }

  const res = await http.get<{ data: { asset: Asset; url: string } }>(`/assets/${assetId}/url`);
  const data = unwrap(res);
  return { url: data.url };
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

export async function listUsers(params: {
  q?: string;
  page?: number;
  pageSize?: number;
} = {}): Promise<PaginatedResult<UserProfile>> {
  const res = await http.get<{ data: PaginatedResult<UserProfile> }>('/users', { params });
  return unwrap(res);
}

export async function getUser(id: string): Promise<UserProfile> {
  const res = await http.get<{ data: UserProfile }>(`/users/${id}`);
  return unwrap(res);
}

export async function listTeamMemberOptions(params: {
  q?: string;
  page?: number;
  pageSize?: number;
  excludeTeamId?: string;
} = {}): Promise<PaginatedResult<TeamMemberOption>> {
  const res = await http.get<{ data: PaginatedResult<TeamMemberOption> }>(
    '/teams/member-options',
    { params },
  );
  return unwrap(res);
}

export async function listUserOptions(): Promise<UserOption[]> {
  const res = await http.get<{ data: UserOption[] }>('/users/options');
  return unwrap(res);
}

export async function listSystemRoleOptionsForStaff(): Promise<
  { id: string; name: string; description: string | null }[]
> {
  const res = await http.get<{
    data: { id: string; name: string; description: string | null }[];
  }>('/users/system-role-options');
  return unwrap(res);
}

export async function createUser(payload: CreateUserPayload): Promise<UserProfile> {
  const res = await http.post<{ data: UserProfile }>('/users', payload);
  return unwrap(res);
}

export async function downloadStaffBulkTemplate(): Promise<void> {
  const res = await http.get('/users/bulk-template', { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'staff-upload-template.xlsx';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function uploadStaffBulk(file: File): Promise<StaffBulkImportResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await http.postForm<{ data: StaffBulkImportResult }>('/users/bulk', form);
  return unwrap(res);
}

export async function updateUser(id: string, payload: UpdateUserPayload): Promise<UserProfile> {
  const res = await http.patch<{ data: UserProfile }>(`/users/${id}`, payload);
  return unwrap(res);
}

export async function assignUserDepartment(
  userId: string,
  departmentId: string,
  role: string,
): Promise<UserProfile> {
  const res = await http.post<{ data: UserProfile }>(`/users/${userId}/departments`, {
    departmentId,
    role,
  });
  return unwrap(res);
}

export async function removeUserDepartment(
  userId: string,
  departmentId: string,
  role: string,
): Promise<UserProfile> {
  const res = await http.delete<{ data: UserProfile }>(
    `/users/${userId}/departments/${departmentId}/${role}`,
  );
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// Agent availability
// ---------------------------------------------------------------------------

export async function getMyAgentStatus(): Promise<{
  agentStatus: string;
  statusUpdatedAt: string | null;
}> {
  const res = await http.get<{
    data: { agentStatus: string; statusUpdatedAt: string | null };
  }>('/agents/me/status');
  return unwrap(res);
}

export async function updateMyAgentStatus(status: string): Promise<{
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  agentStatus: string;
  statusUpdatedAt: string | null;
}> {
  const res = await http.patch<{
    data: {
      id: string;
      firstName: string;
      lastName: string;
      name: string;
      agentStatus: string;
      statusUpdatedAt: string | null;
    };
  }>('/agents/me/status', { status });
  return unwrap(res);
}

export async function updateAgentStatus(
  agentId: string,
  status: string,
): Promise<{
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  agentStatus: string;
  statusUpdatedAt: string | null;
}> {
  const res = await http.patch<{
    data: {
      id: string;
      firstName: string;
      lastName: string;
      name: string;
      agentStatus: string;
      statusUpdatedAt: string | null;
    };
  }>(`/agents/${agentId}/status`, { status });
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// ERP entities
// ---------------------------------------------------------------------------

export async function listAssignableMembers(
  departmentId: string,
): Promise<AssignableTeamGroup[]> {
  const res = await http.get<{ data: AssignableTeamGroup[] }>(
    `/departments/${departmentId}/assignable-members`,
  );
  return unwrap(res);
}

export async function searchEntityParents(
  q: string,
  source: LinkedSource,
): Promise<{ results: EntityParentResult[]; error?: 'source_unavailable' }> {
  const res = await http.get<{ data: { results: EntityParentResult[]; error?: 'source_unavailable' } }>(
    '/entity-search/parents',
    { params: { q, source } },
  );
  return unwrap(res);
}

export async function searchEntityLearners(
  parentId: string,
  source: LinkedSource,
): Promise<{ results: EntityLearnerResult[]; error?: 'source_unavailable' }> {
  const res = await http.get<{ data: { results: EntityLearnerResult[]; error?: 'source_unavailable' } }>(
    '/entity-search/learners',
    { params: { parentId, source } },
  );
  return unwrap(res);
}

export async function searchEntityByEnrollment(
  q: string,
  source: LinkedSource,
): Promise<{ results: EnrollmentLookupResult[]; error?: 'source_unavailable' }> {
  const res = await http.get<{
    data: { results: EnrollmentLookupResult[]; error?: 'source_unavailable' };
  }>('/entity-search/enrollment', {
    params: { q, source },
  });
  return unwrap(res);
}

export async function listEnrollmentSourceRules(): Promise<EnrollmentSourceRulesResponse> {
  const res = await http.get<{ data: EnrollmentSourceRulesResponse }>(
    '/entity-search/enrollment-rules',
  );
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// Entity sources (platform config)
// ---------------------------------------------------------------------------

export async function listEntitySources(): Promise<EntitySourceSummary[]> {
  const res = await http.get<{ data: EntitySourceSummary[] }>('/entity-sources');
  return unwrap(res);
}

export async function getEntitySource(id: string): Promise<EntitySourceDetail> {
  const res = await http.get<{ data: EntitySourceDetail }>(`/entity-sources/${id}`);
  return unwrap(res);
}

export async function createEntitySource(payload: {
  code: string;
  name: string;
  description?: string;
  sortOrder?: number;
  isActive?: boolean;
}): Promise<EntitySourceSummary> {
  const res = await http.post<{ data: EntitySourceSummary }>('/entity-sources', payload);
  return unwrap(res);
}

export async function updateEntitySource(
  id: string,
  payload: {
    name?: string;
    description?: string | null;
    sortOrder?: number;
    isActive?: boolean;
  },
): Promise<EntitySourceSummary> {
  const res = await http.patch<{ data: EntitySourceSummary }>(`/entity-sources/${id}`, payload);
  return unwrap(res);
}

export async function addEntitySourcePrefix(
  sourceId: string,
  payload: { prefix: string; description?: string; sortOrder?: number; isActive?: boolean },
): Promise<EntitySourceDetail> {
  const res = await http.post<{ data: EntitySourceDetail }>(
    `/entity-sources/${sourceId}/prefixes`,
    payload,
  );
  return unwrap(res);
}

export async function updateEntitySourcePrefix(
  prefixId: string,
  payload: {
    prefix?: string;
    description?: string | null;
    sortOrder?: number;
    isActive?: boolean;
  },
): Promise<EntitySourcePrefix> {
  const res = await http.patch<{ data: EntitySourcePrefix }>(
    `/entity-sources/prefixes/${prefixId}`,
    payload,
  );
  return unwrap(res);
}

export async function removeEntitySourcePrefix(prefixId: string): Promise<EntitySourceDetail> {
  const res = await http.delete<{ data: EntitySourceDetail }>(
    `/entity-sources/prefixes/${prefixId}`,
  );
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// Intake routes (platform config)
// ---------------------------------------------------------------------------

export async function listIntakeRoutes(): Promise<IntakeRouteSummary[]> {
  const res = await http.get<{ data: IntakeRouteSummary[] }>('/intake-routes');
  return unwrap(res);
}

export async function getIntakeRoute(id: string): Promise<IntakeRouteSummary> {
  const res = await http.get<{ data: IntakeRouteSummary }>(`/intake-routes/${id}`);
  return unwrap(res);
}

export async function createIntakeRoute(payload: {
  name?: string;
  channelTypeId: string;
  audience?: import('@21k/shared').TicketAudience | null;
  categoryId?: string | null;
  erpSchoolId?: string | null;
  primaryDepartmentId: string;
  sortOrder?: number;
  isActive?: boolean;
}): Promise<IntakeRouteSummary> {
  const res = await http.post<{ data: IntakeRouteSummary }>('/intake-routes', payload);
  return unwrap(res);
}

export async function updateIntakeRoute(
  id: string,
  payload: Partial<{
    name?: string;
    channelTypeId: string;
    audience?: import('@21k/shared').TicketAudience | null;
    categoryId?: string | null;
    erpSchoolId?: string | null;
    primaryDepartmentId: string;
    sortOrder?: number;
    isActive?: boolean;
  }>,
): Promise<IntakeRouteSummary> {
  const res = await http.patch<{ data: IntakeRouteSummary }>(`/intake-routes/${id}`, payload);
  return unwrap(res);
}

export async function resolveIntakeRoute(params: {
  channelTypeId: string;
  audience?: import('@21k/shared').TicketAudience;
  categoryId?: string;
  erpSchoolId?: string;
}): Promise<IntakeResolveResult> {
  const res = await http.get<{ data: IntakeResolveResult }>('/intake-routes/resolve', {
    params,
  });
  return unwrap(res);
}

/** @deprecated Use searchEntityParents with source ERP */
export async function searchParents(query: string): Promise<ParentResult[]> {
  const res = await http.get<{ data: ParentResult[] }>('/erp-entities/parents', {
    params: { q: query },
  });
  return unwrap(res);
}

export async function getLearnersByParent(parentId: string): Promise<
  { id: string; firstName: string; lastName: string }[]
> {
  const res = await http.get<{
    data: { id: string; firstName: string; lastName: string }[];
  }>(`/erp-entities/parents/${parentId}/learners`);
  return unwrap(res);
}

export async function getLearnerContext(learnerId: string): Promise<LearnerContext | null> {
  const res = await http.get<{ data: LearnerContext | null }>(
    `/erp-entities/learners/${learnerId}/context`,
  );
  return unwrap(res);
}

// ---------------------------------------------------------------------------
// Dashboard & Reports
// ---------------------------------------------------------------------------

export async function getDashboard(params?: {
  departmentId?: string;
  teamId?: string;
}): Promise<DashboardPayload> {
  const res = await http.get<{ data: DashboardPayload }>('/dashboard', { params });
  return unwrap(res);
}

export async function getReport(
  reportType: ReportType,
  params: { dateFrom: string; dateTo: string; departmentId?: string },
): Promise<Record<string, unknown>> {
  const res = await http.get<{ data: Record<string, unknown> }>(`/reports/${reportType}`, {
    params,
  });
  return unwrap(res);
}

export async function exportReport(payload: {
  reportType: ReportType;
  dateFrom: string;
  dateTo: string;
  departmentId?: string;
}): Promise<Blob> {
  const res = await http.post('/reports/export', payload, { responseType: 'blob' });
  return res.data as Blob;
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export async function listNotifications(page = 1): Promise<NotificationListResponse> {
  const res = await http.get<{ data: NotificationListResponse }>('/notifications', {
    params: { page },
  });
  return unwrap(res);
}

export async function markNotificationRead(id: string): Promise<void> {
  await http.patch(`/notifications/${id}/read`);
}

export async function markAllNotificationsRead(): Promise<void> {
  await http.patch('/notifications/read-all');
}
